// Form controls (UX03). Owner: WP04. Every control has a visible label; errors are text linked by aria-describedby.
import { useId, useState, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';
import { Eye, EyeOff } from 'lucide-react';

export type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'prefix'> & {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  /** Content inside the field before the input (an icon or a fixed "https://" prefix). */
  leading?: ReactNode;
  ref?: Ref<HTMLInputElement>;
};

/** Labelled text input. type="password" adds a Show/Hide toggle (password managers and paste keep working). */
export function TextField({ label, hint, error, leading, type = 'text', className, ...rest }: TextFieldProps) {
  const id = useId();
  const [reveal, setReveal] = useState(false);
  const isPassword = type === 'password';
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div className={['ui-field', error ? 'has-error' : '', className ?? ''].filter(Boolean).join(' ')}>
      <label className="ui-field__label" htmlFor={id}>{label}</label>
      <div className="ui-field__control">
        {leading ? <span className="ui-field__prefix" aria-hidden>{leading}</span> : null}
        <input id={id} type={isPassword && reveal ? 'text' : type} aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...rest} />
        {isPassword ? (
          <button type="button" className="ui-field__reveal" aria-label={reveal ? 'Hide password' : 'Show password'} aria-controls={id} onClick={() => setReveal((r) => !r)}>
            {reveal ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
            <span aria-hidden>{reveal ? 'Hide' : 'Show'}</span>
          </button>
        ) : null}
      </div>
      {hint ? <p className="ui-field__hint" id={`${id}-hint`}>{hint}</p> : null}
      {error ? <p className="ui-field__error" id={`${id}-error`}>{error}</p> : null}
    </div>
  );
}

export type SwitchProps = { label: string; description?: ReactNode; checked: boolean; onChange(checked: boolean): void; disabled?: boolean };

/** On/off switch (role=switch) with a visible label and optional explanation. The whole row is the target. */
export function Switch({ label, description, checked, onChange, disabled }: SwitchProps) {
  const id = useId();
  return (
    <div className="ui-switch-row">
      <div className="ui-switch-row__text">
        <span id={`${id}-label`} className="ui-switch-row__label">{label}</span>
        {description ? <span id={`${id}-desc`} className="ui-switch-row__desc">{description}</span> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={description ? `${id}-desc` : undefined}
        className="ui-switch"
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className="ui-switch__thumb" aria-hidden />
        <span className="ui-switch__state" aria-hidden>{checked ? 'On' : 'Off'}</span>
      </button>
    </div>
  );
}

export type SelectOption = { value: string; label: string; disabled?: boolean };
export type SelectProps = {
  label: string;
  value: string;
  options: SelectOption[];
  onChange(value: string): void;
  /** Keep the label for screen readers only (compact toolbars). */
  hideLabel?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
  className?: string;
};

/** Native select (accessible and mobile-friendly) styled to match. */
export function Select({ label, value, options, onChange, hideLabel, icon, disabled, className }: SelectProps) {
  const id = useId();
  return (
    <div className={['ui-select', className ?? ''].filter(Boolean).join(' ')}>
      <label htmlFor={id} className={hideLabel ? 'visually-hidden' : 'ui-field__label'}>{label}</label>
      <div className="ui-select__control">
        {icon ? <span className="ui-select__icon" aria-hidden>{icon}</span> : null}
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
          {options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
}
